<template>
  <div>
    <q-linear-progress
      v-if="loading"
      color="primary"
      indeterminate
    />

    <div
      v-if="persons.length"
      class="row q-col-gutter-md"
    >
      <div
        v-for="person in persons"
        :key="person.id"
        class="col-xs-6 col-sm-4 col-md-3 col-lg-2"
      >
        <PersonCard
          :person="person"
          :selectable="mergeMode"
          :selected="mergeSelectedIds.includes(person.id)"
          @click="viewPerson"
          @edit="openEditModal"
          @trash="openTrashModal"
          @toggle="toggleMergeSelect"
        />
      </div>
    </div>

    <div
      v-else-if="!loading"
      class="text-center text-grey-6 q-pa-xl"
    >
      <q-icon
        name="fa fa-users"
        size="64px"
        class="q-mb-md"
      />
      <div>No people detected yet</div>
    </div>

    <InfiniteScroll
      :active="hasMore"
      :loading="loadingMore"
      class="q-mt-md"
      @load-more="loadMore"
    />

    <div
      v-if="canMerge && mergeSelectedIds.length >= 2"
      class="q-mt-md text-center"
    >
      <q-btn
        color="primary"
        icon="fa fa-compress-arrows-alt"
        :label="`Merge ${mergeSelectedIds.length} Persons`"
        @click="openMergeModal"
      />
    </div>
    <div
      v-else-if="canMerge"
      class="q-mt-md text-center text-grey-6 text-caption"
    >
      Select at least 2 persons to merge
    </div>

    <PersonDetailDialog
      ref="personDetailDialog"
      :client-id="clientId"
      @updated="onPersonUpdated"
    />

    <EditPersonModal
      ref="editPersonModal"
      @updated="onPersonUpdated"
    />

    <TrashPersonModal
      ref="trashPersonModal"
      @trashed="onPersonTrashed"
    />

    <MergePersonsModal
      ref="mergePersonsModal"
      @merged="onPersonsMerged"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import _ from 'lodash';
import { Person } from '@/analysis/types';
import isGranted from '@/auth/isGranted';
import InfiniteScroll from '@/components/InfiniteScroll.vue';
import PersonCard from './PersonCard.vue';
import PersonDetailDialog from './PersonDetailDialog.vue';
import EditPersonModal from './EditPersonModal.vue';
import TrashPersonModal from './TrashPersonModal.vue';
import MergePersonsModal from './MergePersonsModal.vue';
import personRepository from './person-repository';

interface Data {
  persons: Person[];
  loading: boolean;
  loadingMore: boolean;
  total: number;
  offset: number;
  mergeSelectedIds: number[];
}

const PAGE_SIZE = 24;

export default defineComponent({
  emits: ['merge-complete', 'person-trashed'],
  components: {
    InfiniteScroll,
    PersonCard,
    PersonDetailDialog,
    EditPersonModal,
    TrashPersonModal,
    MergePersonsModal,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    keyword: {
      type: String,
      default: '',
    },
    mergeMode: {
      type: Boolean,
      default: false,
    },
  },
  data(): Data {
    return {
      persons: [],
      loading: false,
      loadingMore: false,
      total: 0,
      offset: 0,
      mergeSelectedIds: [],
    };
  },
  computed: {
    hasMore(): boolean {
      return this.persons.length < this.total;
    },
    canMerge(): boolean {
      return this.mergeMode && isGranted('MANAGE_PERSONS');
    },
  },
  watch: {
    keyword: _.debounce(function dLoad(this: any) {
      this.load();
    }, 1500),
    mergeMode(val: boolean) {
      if (!val) {
        this.mergeSelectedIds = [];
      }
    },
  },
  created() {
    this.load();
  },
  methods: {
    isGranted,
    async load() {
      this.loading = true;
      try {
        const params: Record<string, unknown> = {
          limit: PAGE_SIZE,
          offset: 0,
        };
        if (this.keyword) params.keyword = this.keyword;
        const result = await personRepository.findAll(this.clientId, params);
        this.persons = result.items;
        this.total = result.total;
        this.offset = PAGE_SIZE;
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      this.loadingMore = true;
      try {
        const result = await personRepository.findAll(this.clientId, {
          limit: PAGE_SIZE,
          offset: this.offset,
        });
        this.persons.push(...result.items);
        this.offset += PAGE_SIZE;
      } finally {
        this.loadingMore = false;
      }
    },

    viewPerson(person: Person) {
      (this.$refs.personDetailDialog as any).open(person);
    },

    openEditModal(person: Person) {
      (this.$refs.editPersonModal as any).open(person);
    },

    openTrashModal(person: Person) {
      (this.$refs.trashPersonModal as any).open(person);
    },

    toggleMergeSelect(person: Person) {
      const index = this.mergeSelectedIds.indexOf(person.id);
      if (index !== -1) {
        this.mergeSelectedIds.splice(index, 1);
      } else {
        this.mergeSelectedIds.push(person.id);
      }
    },

    openMergeModal() {
      const selected = this.persons.filter(p => this.mergeSelectedIds.includes(p.id));
      (this.$refs.mergePersonsModal as any).open(selected);
    },

    onPersonUpdated(person: Person) {
      const index = this.persons.findIndex(p => p.id === person.id);
      if (index !== -1) {
        this.persons.splice(index, 1, person);
      }
    },

    onPersonTrashed(person: Person) {
      this.persons = this.persons.filter(p => p.id !== person.id);
      this.total -= 1;
      this.$emit('person-trashed', person);
    },

    onPersonsMerged({ target, deletedIds }: { target: Person; deletedIds: number[] }) {
      this.persons = this.persons.filter(p => !deletedIds.includes(p.id));
      const index = this.persons.findIndex(p => p.id === target.id);
      if (index !== -1) {
        this.persons.splice(index, 1, target);
      }
      this.total -= deletedIds.length;
      this.$emit('merge-complete');
    },
  },
});
</script>
